import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import VehiclesSubNav from "./VehiclesSubNav";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { CalendarClock, CheckCircle, AlertTriangle } from "lucide-react";

export default function Inspections() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState({});
  const [tab, setTab] = useState("upcoming");

  const load = async () => {
    try {
      const params = tab === "upcoming" ? { upcoming: true, days: 90 } : {};
      const { data } = await api.get("/vehicle-inspections", { params });
      setItems(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  useEffect(() => {
    if (user) {
      load();
      api.get("/vehicles").then((r) => {
        const map = {};
        (r.data || []).forEach((v) => { map[v.id] = v; });
        setVehicles(map);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tab]);

  if (!user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;

  const setResult = async (insp, result) => {
    let next_inspection = null;
    if (result === "pass") {
      const next = new Date();
      next.setFullYear(next.getFullYear() + 1);
      next_inspection = next.toISOString();
    }
    try {
      await api.patch(`/vehicle-inspections/${insp.id}`, { result, next_inspection });
      toast.success(t("veh.updated"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16">
        <div className="mb-6">
          <div className="label-tech">{t("veh.label")}</div>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("vins.title")}</h1>
        </div>

        <VehiclesSubNav />

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setTab("upcoming")}
            className={`px-3 py-1.5 text-xs font-mono-tech uppercase tracking-[0.1em] border ${tab === "upcoming" ? "bg-[var(--ink)] text-[var(--bone)] border-[var(--ink)]" : "border-[var(--ink-200)]"}`}
            data-testid="vins-tab-upcoming"
          >
            {t("vins.upcoming")}
          </button>
          <button
            onClick={() => setTab("all")}
            className={`px-3 py-1.5 text-xs font-mono-tech uppercase tracking-[0.1em] border ${tab === "all" ? "bg-[var(--ink)] text-[var(--bone)] border-[var(--ink)]" : "border-[var(--ink-200)]"}`}
            data-testid="vins-tab-all"
          >
            {t("vins.all")}
          </button>
        </div>

        <div className="border border-[var(--ink-200)] bg-[var(--bone)]" data-testid="inspections-table">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 label-tech border-b border-[var(--ink-200)]">
            <div className="col-span-3">{t("vins.date")}</div>
            <div className="col-span-3">{t("veh.regnr")}</div>
            <div className="col-span-3">{t("vins.result")}</div>
            <div className="col-span-3 text-right">—</div>
          </div>
          {items.length === 0 ? (
            <div className="p-10 label-tech text-center">{t("vins.empty")}</div>
          ) : items.map((i) => {
            const v = vehicles[i.vehicle_id];
            return (
              <div key={i.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[var(--ink-200)] text-sm items-center" data-testid={`vins-row-${i.id}`}>
                <div className="col-span-3 font-mono-tech">{i.date?.slice(0, 10)}</div>
                <div className="col-span-3">
                  <Link to={`/vehicles/${i.vehicle_id}`} className="font-mono-tech underline decoration-dotted">{v?.regnr || i.vehicle_id.slice(0, 8)}</Link>
                  {i.is_reinspection && <div className="text-[10px] uppercase tracking-wider text-[var(--clay)]">{t("vins.reinspection")}</div>}
                </div>
                <div className="col-span-3">
                  {i.result === "pass" && <span className="inline-flex items-center gap-1 text-[var(--moss)]"><CheckCircle size={12} /> {t("vins.pass")}</span>}
                  {i.result === "fail" && <span className="inline-flex items-center gap-1 text-[var(--brick)]"><AlertTriangle size={12} /> {t("vins.fail")}</span>}
                  {!i.result && <span className="inline-flex items-center gap-1 text-[var(--ink-500)]"><CalendarClock size={12} /> {t("vins.pending")}</span>}
                </div>
                <div className="col-span-3 flex justify-end gap-2">
                  {!i.result && (
                    <>
                      <button onClick={() => setResult(i, "pass")} className="btn-ghost text-xs text-[var(--moss)]" data-testid={`vins-pass-${i.id}`}>{t("vins.markPass")}</button>
                      <button onClick={() => setResult(i, "fail")} className="btn-ghost text-xs text-[var(--brick)]" data-testid={`vins-fail-${i.id}`}>{t("vins.markFail")}</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
